import {
  FormkitComponent,
  FormkitNode,
  ModifierType,
  NodeType,
  SEPARATE_FORM_CONTEXTS,
  isStateReference,
  resolveBooleanCondition,
} from "@hightouch/formkit";
import { formkitRenderer } from "@hightouch/lib/templating/formkit-renderer";
import { merge } from "lodash";
import { FieldValues, UseFormWatch } from "react-hook-form";

// Collects StateReference keys from a Formkit entity so callers can re-resolve it when those fields change.
// Skips SEPARATE_FORM_CONTEXTS subtrees, which resolve against a nested form's own state.
export function collectStateReferenceKeys(entity: unknown): string[] {
  const keys = new Set<string>();
  const walk = (value: unknown) => {
    if (value === null || typeof value !== "object") {
      return;
    }
    if (isStateReference(value)) {
      keys.add(value.key);
    } else if (Array.isArray(value)) {
      value.forEach(walk);
    } else {
      for (const [key, child] of Object.entries(value)) {
        if (!SEPARATE_FORM_CONTEXTS.includes(key)) {
          walk(child);
        }
      }
    }
  };
  walk(entity);
  return [...keys];
}

export const getNestedKeys = (node: FormkitNode) => {
  if (node.type === NodeType.Component) {
    return node.key;
  } else {
    return node.children.map(getNestedKeys);
  }
};

export const getNestedKeysWithDefault = (
  node: FormkitNode,
  context: Record<string, unknown>,
  watch: UseFormWatch<FieldValues>,
): Record<string, any> => {
  // Container components (Tabs, Collapsible, Template) carry children.
  // Recurse so defaults for fields nested inside them are seeded too.
  if (node.type === NodeType.Component) {
    return merge(
      { [node.key]: node.props?.default },
      ...(node.children ?? []).map((child) =>
        getNestedKeysWithDefault(child, context, watch),
      ),
    );
  }

  // Exit early if we are in a ShowModifier which is currently hidden
  if (
    node.type === NodeType.Modifier &&
    node.modifier === ModifierType.Show &&
    resolveBooleanCondition(node.condition, formkitRenderer)(watch, context) !==
      true
  ) {
    return {};
  }

  // Iterate over all children and merge into one record
  return merge(
    {},
    ...node.children.map((child) =>
      getNestedKeysWithDefault(child, context, watch),
    ),
  );
};

export const collectVisibleKeys = (
  node: FormkitNode,
  context: Record<string, unknown>,
  watch: UseFormWatch<FieldValues>,
): Set<string> =>
  new Set(Object.keys(getNestedKeysWithDefault(node, context, watch)));

export function collectDescendantComponents(
  node: FormkitNode,
): FormkitComponent[] {
  const roots = node.children ?? [];
  if (roots.length === 0) {
    return [];
  }

  const result: FormkitComponent[] = [];

  const walk = (nodes: FormkitNode[]) => {
    nodes.forEach((n) => {
      if (n.type === NodeType.Component) {
        result.push(n);
      }

      const children = n.children ?? [];
      if (children.length > 0) {
        walk(children);
      }
    });
  };

  walk(roots);
  return result;
}
